// ------------------------------
// components/Receive.tsx
// Receive-payment screen with tiny-screen QR-code scaling
// ------------------------------

import { observer } from "mobx-react";
import React from "react";
import {
  Text,
  Button,
  Center,
  Stack,
  AspectRatio,
  Group,
  CopyButton,
  ActionIcon,
  Title,
  Box,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconCopy, IconCheck, IconBolt } from "@tabler/icons-react";
import { QRCodeSVG } from "qrcode.react";

import { BobsWorkspace } from "../stores/BobsWorkspace";
import { BOB_MODALS } from "../helpers/constants";
import DownloadQRButton from "./DownloadQRButton";

const Receive = observer(
  ({ bobsWorkspace }: { bobsWorkspace: BobsWorkspace }) => {
    const { selectedBob } = bobsWorkspace;
    const payLink = `lightning:${selectedBob.lightningAddress}`;

    /*  Detect “tiny” screens (≤ 320 px wide). JioPhone 2 is 240 px.  */
    const isTinyScreen = useMediaQuery("(max-width: 320px)");

    /*  QR size: 300 px everywhere except tiny screens (shrink to 200 px).  */
    const qrSize = isTinyScreen ? 200 : 300;

    return (
      <Stack>
        <Center>
          <Group gap="xs">
            <IconBolt size={28} />
            <Title order={3}>Receive Lightning</Title>
          </Group>
        </Center>

        <Center>
          <AspectRatio ratio={1} style={{ maxWidth: qrSize }}>
            <a href={payLink} style={{ display: "block" }}>
              <QRCodeSVG
                value={payLink}
                size={qrSize}
                bgColor="#ffffff"
                marginSize={2}
                fgColor="#000000"
                level="L"
                title="Pay to this address"
              />
            </a>
          </AspectRatio>
        </Center>

        <Center>
          <Group gap="xs" align="center">
            <Text
              component="a"
              href={payLink}
              size="sm"
              style={{ wordBreak: "break-all" }}
            >
              {selectedBob.lightningAddress}
            </Text>
            <CopyButton value={selectedBob.lightningAddress}>
              {({ copied, copy }) => (
                <ActionIcon
                  size="lg"
                  variant={copied ? "filled" : "default"}
                  color={copied ? "teal" : "blue"}
                  onClick={copy}
                  aria-label="copy address"
                >
                  {copied ? <IconCheck /> : <IconCopy />}
                </ActionIcon>
              )}
            </CopyButton>
          </Group>
        </Center>

        <Box mx="auto" w="100%" maw={qrSize}>
          <Stack gap="sm">
            <Button
              w="100%"
              onClick={() =>
                selectedBob.setOpenModal(BOB_MODALS.SET_RECEIVE_AMOUNT)
              }
            >
              Set Invoice Amount
            </Button>

            <DownloadQRButton
              lightningAddress={selectedBob.lightningAddress}
              qrSize={qrSize}
            />
          </Stack>
        </Box>
      </Stack>
    );
  }
);

export default Receive;
